/**
 * =============================================================================
 * MISSION CONTROL LAYOUT CONFIGURATIONS - AI-NATIVE DESIGN SYSTEM
 * =============================================================================
 * 
 * Pre-defined layout configurations for Mission Control pages.
 * 
 * DESIGN SYSTEM:
 * - 8pt spacing grid (8, 16, 24, 32px)
 * - Semantic colors only (Red/Amber/Green/Blue)
 * - Typography: Page 28-32px, Section 18-20px, Card 16-18px, KPI 32-36px
 * - Action-driven layout, not report-driven
 * 
 * LAYOUT STRUCTURE:
 * 1. Control Metrics (top) - Key metrics at a glance
 * 2. Top Stories - Full-width critical alerts
 * 3. AI Spotlight - Featured recommendation
 * 4. Pending Actions - Secondary pending items
 */

import type {
    PageLayoutConfig,
    WatchCardConfig,
    WatchlistConfig,
    AISpotlightWidgetConfig,
} from '../types';
import { HOME_PAGE_METRICS } from '../config/home-page.metrics';

// =============================================================================
// SPACING TOKENS (8pt grid)
// =============================================================================
const spacing = {
    xs: 8,
    sm: 16,
    md: 24,
    lg: 32,
} as const;

// Home page metric instances live in config/home-page.metrics.ts
export { HOME_PAGE_METRICS };

// =============================================================================
// WATCH CARD CONFIGURATIONS (Top Stories)
// =============================================================================
export const DEFAULT_WATCH_CARDS: WatchCardConfig[] = [
    {
        id: 'watch-card-1',
        // Merchant-agnostic: Search by alertType + generatedBy instead of alertId
        alertType: 'SKU_UNDERSTOCK',
        generatedBy: 'SYSTEM',
        categoryLabel: 'Replenishment',
        priority: 'urgent',
        dataSource: {
            endpoint: '/api/v1/check-list-items/list',
            method: 'GET',
            headingKey: 'heading',
            summaryKey: 'summary',
        },
        // No fallback - show "Activate Story" when no data
        onClickPath: '/alert/:alertId',
        isEditable: true,
        editConfig: {
            reference: {
                title: 'Replenishment Alert',
            },
            agentConfig: {
                endpoint: '/api/v1/agent/alert-editor',
                method: 'POST',
                agentType: 'alert-editor',
            },
        },
    },
    {
        id: 'watch-card-2',
        // Merchant-agnostic: Search by alertType + generatedBy instead of alertId
        alertType: 'SKU_OVERSTOCK',
        generatedBy: 'SYSTEM',
        categoryLabel: 'Reorder',
        priority: 'high',
        dataSource: {
            endpoint: '/api/v1/check-list-items/list',
            method: 'GET',
            headingKey: 'heading',
            summaryKey: 'summary',
        },
        // No fallback - show "Activate Story" when no data
        onClickPath: '/alert/:alertId',
        isEditable: true,
        editConfig: {
            reference: {
                title: 'Reorder Alert',
            },
            agentConfig: {
                endpoint: '/api/v1/agent/alert-editor',
                method: 'POST',
                agentType: 'alert-editor',
            },
        },
    },
    {
        id: 'watch-card-3',
        categoryLabel: 'Forecast Accuracy',
        priority: 'medium',
        isConfigured: false, // Not configured yet - shows "Coming Soon!" CTA
        dataSource: {
            endpoint: '/api/v1/check-list-items/create',
            method: 'POST',
            headingKey: 'heading',
            summaryKey: 'summary',
        },
        onClickPath: '/alert/:alertId',
        isEditable: true,
        editConfig: {
            reference: {
                title: 'Coverage Alert',
            },
            agentConfig: {
                endpoint: '/api/v1/agent/alert-editor',
                method: 'POST',
                agentType: 'alert-editor',
            },
        },
    },
];

// =============================================================================
// AI SPOTLIGHT CARD (Featured) - Legacy, kept for backward compatibility
// =============================================================================
export const DEFAULT_STANDALONE_CARD: WatchCardConfig = {
    id: 'standalone-featured',
    alertId: 'alert-featured-001',
    categoryLabel: 'Recommendation',
    priority: 'medium',
    dataSource: {
        endpoint: '/api/v1/check-list-items/create',
        method: 'POST',
        headingKey: 'heading',
        summaryKey: 'summary',
    },
    fallback: {
        heading: 'Optimize warehouse allocation for Q1',
        summary: 'Based on seasonal patterns, redistributing 2,400 SKUs across your network could reduce stockouts by 18% and improve fill rate.',
    },
    onClickPath: '/alert/:alertId',
    isEditable: true,
    editConfig: {
        reference: {
            title: 'AI Recommendation',
        },
        agentConfig: {
            endpoint: '/api/v1/agent/alert-editor',
            method: 'POST',
            agentType: 'alert-editor',
        },
    },
};

// =============================================================================
// AI SPOTLIGHT WIDGET CONFIG (New Design with 3 Cards)
// =============================================================================
export const DEFAULT_AI_SPOTLIGHT_CONFIG: AISpotlightWidgetConfig = {
    summary: 'AI-powered insights to help you stay ahead of supply chain challenges.',
    cards: [
        // Card 1: Expected Stock-outs (EXPECTED_UNDERSTOCK)
        {
            id: 'ai-spotlight-card-1',
            summary: 'Expected Stock-outs',
            icon: 'heartbeat',
            dataSource: {
                endpoint: '/api/v1/check-list-items/list',
                method: 'GET',
                filters: {
                    // Merchant-agnostic: Search by alertType + generatedBy
                    alertType: 'EXPECTED_UNDERSTOCK',
                    generatedBy: 'SYSTEM',
                    statuses: ['CREATED', 'SEEN'],
                },
                mainContentKey: 'heading',
            },
            fallback: {
                mainContent: 'No expected stock-outs',
            },
            drilldownPath: '/alert/:alertId',
        },
        // Card 2: Inventory Pile-up (EXPECTED_OVERSTOCK)
        {
            id: 'ai-spotlight-card-2',
            summary: 'Inventory Pile-up!',
            icon: 'package',
            dataSource: {
                endpoint: '/api/v1/check-list-items/list',
                method: 'GET',
                filters: {
                    // Merchant-agnostic: Search by alertType + generatedBy
                    alertType: 'EXPECTED_OVERSTOCK',
                    generatedBy: 'SYSTEM',
                    statuses: ['CREATED', 'SEEN'],
                },
                mainContentKey: 'heading',
            },
            fallback: {
                mainContent: 'No inventory pile-ups',
            },
            drilldownPath: '/alert/:alertId',
        },
        // Card 3: Lead Time Adherence (LEAD_TIME_ADHERENCE)
        {
            id: 'ai-spotlight-card-3',
            summary: 'Lead Time Adherence',
            icon: 'timer',
            dataSource: {
                endpoint: '/api/v1/check-list-items/list',
                method: 'GET',
                filters: {
                    // Merchant-agnostic: Search by alertType + generatedBy
                    alertType: 'LEAD_TIME_ADHERENCE',
                    generatedBy: 'SYSTEM',
                    statuses: ['CREATED', 'SEEN'],
                },
                mainContentKey: 'heading',
            },
            fallback: {
                mainContent: 'Shipments are on track!',
            },
            drilldownPath: '/alert/:alertId',
        },
    ],
    // System-run daily actions (e.g. the Amazon FBA restock list) as further cards in the same
    // container. User-created alerts stay in Pending Actions.
    systemActions: {
        detailPath: '/alert/:id',
    },
};

// =============================================================================
// WATCHLIST CONFIGURATION
// =============================================================================
export const DEFAULT_WATCHLIST_CONFIG: WatchlistConfig = {
    title: 'Pending Actions',
    subtitle: 'Tasks requiring your attention',
    onItemClickPath: '/alert/:id',
    showInviteButton: false,
    showCount: true,
    emptyMessage: 'All caught up! No pending alerts.',
};

// =============================================================================
// HOME PAGE LAYOUT - AI-NATIVE DESIGN
// =============================================================================
/**
 * Structure:
 * ┌────────────────────────────────────────────────────────────────┐
 * │  Mission Control              Friday, Feb 20                   │
 * │  Good afternoon, {name}                                        │
 * ├────────────────────────────────────────────────────────────────┤
 * │  Control Metrics                                               │
 * │  ┌─────────┐ ┌─────────┐ ┌─────────┐ ┌─────────┐              │
 * │  │ 2.4K    │ │ 890     │ │ ₹1.2M   │ │ ₹320K   │              │
 * │  └─────────┘ └─────────┘ └─────────┘ └─────────┘              │
 * ├────────────────────────────────────────────────────────────────┤
 * │  LEFT (55%)                    │  RIGHT (45%)                  │
 * │  ┌──────────────────────────┐  │  ┌────────────────────────┐  │
 * │  │  Top Stories             │  │  │  Pending Actions       │  │
 * │  │  ▌Card ▌Card ▌Card       │  │  │  ▌Alert                │  │
 * │  ├──────────────────────────┤  │  │  ▌Alert                │  │
 * │  │  AI Spotlight            │  │  │  (scrollable)          │  │
 * │  │  ▌Featured Card          │  │  │                        │  │
 * │  └──────────────────────────┘  │  └────────────────────────┘  │
 * └────────────────────────────────────────────────────────────────┘
 */
export const HOME_PAGE_LAYOUT: PageLayoutConfig = {
    pageId: 'mission-control-home',
    
    header: {
        showGreeting: true,
        title: 'Mission Control',
        showUserName: true,
        rightContent: 'none',
    },
    
    rows: [
        // Row 1: Control Metrics (auto height)
        {
            id: 'row-kpis',
            height: 'auto',
            regions: [
                {
                    id: 'kpi-strip',
                    width: 100,
                    height: 'auto',
                    widget: {
                        type: 'metric-strip',
                        tagline: 'Control Metrics',
                        instances: HOME_PAGE_METRICS,
                    },
                },
            ],
        },
        
        // Row 2: Main content (fills remaining space)
        {
            id: 'row-main',
            height: 100,
            gap: spacing.md,
            regions: [
                // Left column (55%) - Immediate Attention + AI Insights
                {
                    id: 'left-column',
                    width: 55,
                    height: 100,
                    widget: {
                        type: 'custom',
                        children: [
                            // Top Stories - Watch Cards (auto height)
                            {
                                id: 'top-stories',
                                width: 100,
                                height: 'auto',
                                widget: {
                                    type: 'watch-card-grid',
                                    title: 'Top Stories',
                                    cards: DEFAULT_WATCH_CARDS,
                                    columns: 3,
                                    rows: 1,
                                },
                            },
                            // AI Spotlight - Widget with 3 cards + system-run actions (remaining space)
                            {
                                id: 'ai-spotlight',
                                width: 100,
                                height: 1,
                                widget: {
                                    type: 'ai-spotlight-widget',
                                    tagline: 'AI Spotlight',
                                    config: DEFAULT_AI_SPOTLIGHT_CONFIG,
                                },
                            },
                        ],
                    },
                },
                // Right column (45%) - Watchlist with scroll
                {
                    id: 'watchlist',
                    width: 45,
                    height: 100,
                    widget: {
                        type: 'watchlist',
                        config: DEFAULT_WATCHLIST_CONFIG,
                    },
                },
            ],
        },
    ],
    
    padding: {
        horizontal: spacing.lg,
        vertical: spacing.lg,
    },
    
    rowGap: spacing.md,
    backgroundColor: '#FAFAFA',
};
